/* DistinctValuesFeed.js */

"use strict";
const EventEmitter = require("events");
//const _ = require("lodash");

/**
 * @class DistinctValuesFeed
 *
 * @listens DistinctValueChangeFeed#event:DistinctValuesInitialized
 * @listens DistinctValueChangeFeed#event:DistinctValueAdded
 * @listens DistinctValueChangeFeed#event:DistinctValueRemoved
 * @listens DistinctValueChangeFeed#event:Repage
 *
 * @fires DistinctValuesFeed#FeedInitialized
 * @fires DistinctValuesFeed#ValueUpdated
 * @fires DistinctValuesFeed#ValueAdded
 * @fires DistinctValuesFeed#ValueDeleted
 * @fires DistinctValuesFeed#Repage
 * @fires DistinctValuesFeed#Shutdown
 *
 */
class DistinctValuesFeed extends EventEmitter {
    /**
     * @function StartFeed
     * @returns {DistinctValueChangeFeed|undefined}
     *
     * @typedef {string[]} distinctValuesList
     * @typedef {Object.<string, number>} distinctValuesMap
     * @typedef {number} SortDirection
     *
     * @param {Object} [options = {}] - class constructor options
     * @param {Function<StartFeed>} options.startFeed - function to start or recreate distinct values change feed
     */
    constructor(options = {}) {
        super();

        /**
         * @type {DistinctValueChangeFeed|null}
         * @private
         */
        this._feed = null;

        this._replacementValue = options.replacementValue;
        
        /**
         * @type {Function<StartFeed>}
         * @private
         */
        this._startFeed = options.startFeed || function() {};

        /**
         * @type {distinctValuesList}
         * @description List of distinct values
         * @private
         */
        this._valuesList = [];

        /**
         * @type {distinctValuesMap}
         * @description map of values and its amount
         * @private
         */
        this._valuesMap = {};

        /**
         * @type {SortDirection}
         * @description Sort direction could be 1, -1 or 0
         * @private
         */
        this._sortDirection = 1;

        this._onDistinctValuesInitialized = this._onDistinctValuesInitialized.bind(this);
        this._onIncrementValueCount = this._onIncrementValueCount.bind(this);
        this._onDecrementValueCount = this._onDecrementValueCount.bind(this);
        this._onRepage = this._onRepage.bind(this);
        window.DVF = this;
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _triggerFeedInitialized(valuesList, valuesMap) {
        /**
         * DistinctValueChangeFeed has benn initialized
         * @event DistinctValuesFeed#FeedInitialized
         * @param {distinctValuesList} - List of distinct values
         * @param {distinctValuesMap} - Map of values and its amount
         * @param {DistinctValuesFeed} - This class instance
         */
        this.emit("FeedInitialized", valuesList, valuesMap, this);
    }

    _triggerValueAdded(value) {
        /**
         * Value has been added to the list
         * @event DistinctValuesFeed#ValueAdded
         * @param {string} - new added value
         * @param {distinctValuesList}
         * @param {DistinctValuesFeed}
         */
        this.emit("ValueAdded", value, this._valuesList, this);
    }

    _triggerValueDeleted(value) {
        /**
         * Value has been deleted from the list
         * @event DistinctValuesFeed#ValueDeleted
         * @param {string} - deleted value
         * @param {distinctValuesList}
         * @param {DistinctValuesFeed}
         */
        this.emit("ValueDeleted", value, this._valuesList, this);
    }

    _triggerValueUpdated(value) {
        /**
         * Value count has been updated in the list
         * @event DistinctValuesFeed#ValueUpdated
         * @param {string} - updated value
         * @param {number} - value count
         * @param {DistinctValuesFeed}
         */
        this.emit("ValueUpdated", value, this.getValueCount(value), this);
    }

    _triggerShutdown() {
        /**
         * @event DistinctValuesFeed#Shutdown
         * @param {DistinctValuesFeed}
         */
        this.emit("Shutdown", this);
    }

    _triggerRepage() {
        /**
         * The server dropped a burst of per-value events, so every count held here is stale.
         * Relayed rather than handled here because only the owner knows the current query.
         * @event DistinctValuesFeed#Repage
         * @param {DistinctValuesFeed}
         */
        this.emit("Repage", this);
    }

    _registerFeedListeners(feed) {
        feed.addListener("DistinctValuesInitialized", this._onDistinctValuesInitialized)
            .addListener("IncrementValueCount", this._onIncrementValueCount)
            .addListener("DecrementValueCount", this._onDecrementValueCount)
            .addListener("Repage", this._onRepage);
    }

    _unregisterFeedListeners(feed) {
        if (feed) {
            feed.removeListener("DistinctValuesInitialized", this._onDistinctValuesInitialized)
                .removeListener("IncrementValueCount", this._onIncrementValueCount)
                .removeListener("DecrementValueCount", this._onDecrementValueCount)
                .removeListener("Repage", this._onRepage);
        }
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    /**
     * @typedef  {Promise<DistinctValuesFeed|Error>} WhenInitializedPromise
     *
     * @param {DistinctValueChangeFeed} feed - instance of DistinctValueChangeFeed we are waiting
     * @returns {WhenInitializedPromise}
     */
    whenInitialized(feed) {
        return feed.whenInitialized().then(() => this);
    }

    shutdown() {
        this._unregisterFeedListeners(this._feed);
        this._triggerShutdown();
        this.removeAllListeners();

        return Promise.resolve()
            .then(() => this.feedShutdown())
            .then(() => {
                this._feed = null;
                this._valuesList = null;
                this._valuesMap = null;
                this._replacementValue = null;
            });
    }

    feedShutdown(feed = this._feed) {
        this._valuesList = [];
        this._valuesMap = {};
        if (feed) {
            if (feed === this._feed) {
                this._feed = null;
            }
            this._unregisterFeedListeners(feed);
            return feed
                .shutdown()
                .catch(console.error.bind(console))
                .then(() => this);
        } else {
            return Promise.resolve(this);
        }
    }

    /*******************************************/
    /* END PUBLIC COMPONENT MODEL METHODS      */
    /* START HANDLER METHODS.                  */
    /*******************************************/
    _onDistinctValuesInitialized(newData = {}, feed, reinit = false) {
        const oldMap = this._valuesMap;

        this._valuesList = this._sortValues(Object.keys(newData));
        this._valuesMap = newData;

        if (reinit && Object.keys(oldMap).length) {
            this._emitValuesDiff(oldMap, newData);
        }

        this._triggerFeedInitialized(this._valuesList, this._valuesMap);
    }

    _onIncrementValueCount(value) {
        value = String(value);
        const count = this.getValueCount(value) + 1;
        if (count === 1) {
            this._valuesList = this._sortValues(this._valuesList.concat(value));
            this._triggerValueAdded(value);
        }
        this._updateValue(value, count);
    }

    _onDecrementValueCount(value) {
        value = String(value);
        const count = this.getValueCount(value) - 1;
        if (count === 0) {
            this._valuesList = this._valuesList.slice();
            this._valuesList.splice(this._valuesList.indexOf(value), 1);
            this._triggerValueDeleted(value);
        }
        this._updateValue(value, count);
    }

    _onRepage() {
        this._triggerRepage();
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/
    /**
     * Sort distinct values list
     * @param {distinctValuesList} values
     * @returns {distinctValuesList}
     * @private
     */
    _sortValues(values) {
        values = values || this._valuesList;
        const sortDir = this._sortDirection;

        const comparator = (sortDir = 0) => (a, b) => {
            let compareResult = 0;
            if (typeof a === "string" && typeof b === "string") {
                compareResult = a.localeCompare(b, "en", { sensitivity: "accent" }) * sortDir;
            } else if (a !== undefined && b === undefined) {
                compareResult = sortDir;
            } else if (a === undefined && b !== undefined) {
                compareResult = sortDir * -1;
            }

            return compareResult;
        };

        return values.sort(comparator(sortDir));
    }

    /**
     * Update stored count for the "value"
     * @param {String} value - name of the value to be updated
     * @param {number} count - count
     * @returns {DistinctValuesFeed}
     * @private
     */
    _updateValue(value, count) {
        this._valuesMap = Object.assign({}, this._valuesMap, { [value]: count });
        this._triggerValueUpdated(value);
        return this;
    }

    /**
     * Emits the per-value events that would have fired had the change arrived incrementally.
     * Assumes this._valuesMap and this._valuesList already hold newMap.
     * @param {distinctValuesMap} oldMap
     * @param {distinctValuesMap} newMap
     * @private
     */
    _emitValuesDiff(oldMap, newMap) {
        Object.keys(oldMap).forEach((value) => {
            if (!(value in newMap)) {
                this._triggerValueDeleted(value);
            } else if (newMap[value] !== oldMap[value]) {
                this._triggerValueUpdated(value);
            }
        });
        Object.keys(newMap).forEach((value) => {
            if (!(value in oldMap)) {
                this._triggerValueAdded(value);
            }
        });
    }

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/
    /**
     * Sets sort direction for distinct values list
     * @param {SortDirection} value
     * @returns {DistinctValuesFeed}
     *
     */
    setSortDirection(value) {
        const directions = [-1, 1];
        value = directions.includes(value) ? value : 0;

        if (value !== this._sortDirection) {
            this._sortDirection = value;
            this._valuesList = this._sortValues(this._valuesList);
        }
        return this;
    }

    getValues() {
        return this._valuesList;
    }

    getValuesCounts() {
        return this._valuesMap;
    }

    getValueCount(value) {
        return this._valuesMap.hasOwnProperty(value) ? this._valuesMap[value] : 0;
    }

    /**
     * Starts distinct values change feed according to preconfigured start feed function
     * @param {String} fieldName - field name to which distinct values are needed
     * @param {Object} [query] - initial mongo query object
     * @returns {WhenInitializedPromise|Promise<DistinctValuesFeed>}
     */
    startFeed(fieldName, query) {
        const replacementValue = this._replacementValue;
        this.feedShutdown(this._feed);
        
        if (fieldName) {
            this._replacementValue = replacementValue;
            this._feed = this._startFeed(fieldName, query, this._replacementValue);
            
            this._registerFeedListeners(this._feed);
            return this.whenInitialized(this._feed);
        } else {
            return Promise.resolve(this);
        }
    }

    /**
     * Reestablishes current distinct values change feed
     * @param {Object} query - mongo query object
     * @returns {WhenInitializedPromise|Promise<DistinctValuesFeed>}
     */

    reestablishFeed(query) {
        if (!this._feed || this._feed.getStatus() === "SHUTDOWN") {
            return Promise.reject("feed is inactive");
        }
        const tenantId = this._feed.initialRequest.payload.tenantId;
        const requestFailed = new Promise((resolve, reject) => {
            this._feed.reestablishFeed(tenantId, query, undefined, undefined, {Failure: reject});
        });
        return Promise.race([this.whenInitialized(this._feed), requestFailed]);
    }

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}

module.exports = DistinctValuesFeed;
